import { useCallback, useContext } from 'react';
import { GlobalContext } from 'context';
import { getDecryptedValue } from '../utils/LocalStorageUtils';
import {
  useInitiateProxy,
  useTerminateProxy,
  useUpdateProxy,
  ProxyAuthorizationInitiateRequest,
  ProxyInitiateResponse,
  ProxyTerminateRequest,
  ProxyTerminateResponse,
  ProxyAuthorizationRequestStatus,
  ProxyContactPoint,
  ProxyHumanName,
  ProxyIdentifier,
  ProxyConsents,
  UpdateProxyRequest,
  UpdateProxyResponseWrapper,
} from '@sentara-health/sentara-api-hooks-core';
import { isUnAuthorizedError, unAuthorizedErrorHandler } from 'common';


export enum PersonaType {
  Primary = 'Primary',
  Spouse = 'Spouse',
  Dependent = 'Dependents',
  Empty = ''
}

type ProxyConsentPeriod = {
  start?: string;
  end?: string;
  startDate?: string;
  endDate?: string;
  [key: string]: any;
};


export const getPersonaType = (memberId: string): PersonaType => {
  const regex = /\*(\d{2})$/;
  const match = regex.exec(memberId || '');
  if (match) {
    const suffix = match[1];
    if (suffix === '01') return PersonaType.Primary;
    if (suffix === '02') return PersonaType.Spouse;
    return PersonaType.Dependent;
  }
  return PersonaType.Empty;
};

export type InitiateProxyParams = {
  designatorMemberId: string;
  designatorName: string;
  personaType: PersonaType | string;
  contactPoint?: ProxyContactPoint[];
  requestHumanName?: ProxyHumanName;
  proxyIdentifier?: ProxyIdentifier;
  proxyHumanName?: ProxyHumanName;
  proxyContactPoint?: ProxyContactPoint[];
  claims?: string[];
  proxyAuthorizationRequestStatus?: ProxyAuthorizationRequestStatus | string;
  disclosureOption?: string;
  disclosureOptionDescription?: string;
  acknowledgement?: string;
  alcoholAbuseAuthorization?: string;
  consentPeriod?: ProxyConsentPeriod;
};

export type TerminateProxyParams = {
  designatorMemberId?: string;
  designatorLoginId?: string;
  correlationId?: string;
  designateLoginId?: string;
};

export type UpdateProxyParams = {
  designatorLoginId?: string;
  designatorMemberId?: string;
  designateLoginId?: string;
  correlationId?: string;
  proxyContactPoint?: ProxyContactPoint[];
  claims?: string[];
  consentPeriod?: ProxyConsentPeriod;
  disclosureOption?: string;
  disclosureOptionDescription?: string;
  alcoholAbuseAuthorization?: string;
  acknowledgement?: string;
};

const USER_TELECOM_CACHE_KEY = 'userTelecom';

const resolveUserTelecom = async (userTelecom: ProxyContactPoint[] = []) => {
  if (Array.isArray(userTelecom) && userTelecom.length > 0) {
    return userTelecom;
  }

  const cachedTelecom = await getDecryptedValue(USER_TELECOM_CACHE_KEY);
  if (!cachedTelecom) {
    return [];
  }

  if (Array.isArray(cachedTelecom)) {
    return cachedTelecom;
  }

  if (typeof cachedTelecom === 'string') {
    try {
      const parsedTelecom = JSON.parse(cachedTelecom);
      return Array.isArray(parsedTelecom) ? parsedTelecom : [];
    } catch {
      return [];
    }
  }

  return [];
};

export const useProxyAuthorization = () => {
  const { userTelecom } = useContext(GlobalContext);
  const { initiateProxyAuthorization: initiateProxyAPI } = useInitiateProxy();
  const { terminateProxyAuthorization: terminateProxyAPI } =
    useTerminateProxy();
  const { updateProxyAuthorization: updateProxyAPI } = useUpdateProxy();

  const initiateProxy = useCallback(
    async (params: InitiateProxyParams) => {
      const {
        designatorMemberId,
        designatorName,
        personaType,
        contactPoint,
        requestHumanName,
        proxyIdentifier,
        proxyHumanName,
        proxyContactPoint,
        claims,
        proxyAuthorizationRequestStatus,
        disclosureOption,
        disclosureOptionDescription,
        acknowledgement,
        alcoholAbuseAuthorization,
        consentPeriod,
      } = params;

      if (
        !proxyAuthorizationRequestStatus ||
        !disclosureOption ||
        !acknowledgement ||
        !alcoholAbuseAuthorization
      ) {
        throw new Error(
          'Consent fields (status, disclosure option, acknowledgement, alcohol abuse authorization) must be provided explicitly.'
        );
      }

      const finalContactPoint = await resolveUserTelecom(contactPoint ?? userTelecom);
      const nameParts = designatorName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.at(-1) || '';

      const requestData: ProxyAuthorizationInitiateRequest = {
        identifier: [
          {
            system: 'designatorMemberId',
            value: designatorMemberId,
          },
        ],
        humanName: requestHumanName ?? {
          use: 'official',
          text: designatorName,
          family: lastName,
          given: [firstName],
        },
        contactPoint: finalContactPoint,
        proxies: [
          {
            PersonaType: personaType,
            identifier: [
              proxyIdentifier ?? {
                system: 'designatorMemberId',
                value: designatorMemberId,
              },
            ],
            humanName: proxyHumanName,
            contactPoint: proxyContactPoint,
            Consents: [
              {
                proxyAuthorizationRequestStatus,
                claims: claims && claims.length > 0 ? claims : ['Medical claims and authorizations'],
                period: consentPeriod,
                confirmedBy: designatorName,
                designationStatus: 'Designator',
              },
            ],
            DisclosureOption: disclosureOption,
            DisclosureOptionDescription: disclosureOptionDescription ?? '',
            Acknowledgement: acknowledgement,
            AlcoholAbuseAuthorization: alcoholAbuseAuthorization,
          },
        ],
      };

      const response = await initiateProxyAPI(requestData) as ProxyInitiateResponse;

      const errorCode = (response as any)?.errorCode;
      if (errorCode && isUnAuthorizedError(errorCode)) {
        unAuthorizedErrorHandler();
      }

      // Success sets status (201); error responses surface the HTTP status on errorCode.
      const responseStatus = response?.status ?? errorCode;

      // Only a 201 Created is a successful initiate; everything else shows the error modal.
      if (responseStatus !== 201) {
        const initiateError = new Error('Initiate proxy request failed.') as Error & { status?: number };
        initiateError.status = responseStatus;
        throw initiateError;
      }

      return response;
    },
    [initiateProxyAPI, userTelecom]
  );

  const terminateProxy = useCallback(
    async (params: TerminateProxyParams) => {
      const { designatorMemberId, designatorLoginId, correlationId, designateLoginId } = params;

      const identifier: ProxyIdentifier[] = [];
      if (designatorLoginId) {
        identifier.push({ system: 'designatorLoginId', value: designatorLoginId });
      }
      if (correlationId) {
        identifier.push({ system: 'correlationId', value: correlationId });
      }
      if (designateLoginId) {
        identifier.push({ system: 'designateLoginId', value: designateLoginId });
      }
      if (designatorMemberId) {
        identifier.push({ system: 'designatorMemberId', value: designatorMemberId });
      }

      const requestData: ProxyTerminateRequest = { identifier };

      const response = await terminateProxyAPI(requestData) as ProxyTerminateResponse;
      if (!response || response.status >= 400) {
        throw new Error(
          (response as any)?.detail ||
            'Terminate proxy request failed.'
        );
      }

      return response;
    },
    [terminateProxyAPI]
  );

  const updateProxy = useCallback(
    async (params: UpdateProxyParams) => {
      const {
        designatorLoginId,
        designatorMemberId,
        designateLoginId,
        correlationId,
        proxyContactPoint,
        claims,
        consentPeriod,
        disclosureOption,
        disclosureOptionDescription,
        alcoholAbuseAuthorization,
        acknowledgement,
      } = params;

      const rootIdentifier: ProxyIdentifier[] = [];
      if (designatorLoginId) {
        rootIdentifier.push({ system: 'designatorLoginId', value: designatorLoginId });
      }
      if (designatorMemberId) {
        rootIdentifier.push({ system: 'designatorMemberId', value: designatorMemberId });
      }

      const proxyIdentifier: ProxyIdentifier[] = [];
      if (designateLoginId) {
        proxyIdentifier.push({ system: 'designateLoginId', value: designateLoginId });
      }
      if (correlationId) {
        proxyIdentifier.push({ system: 'correlationId', value: correlationId });
      }

      const consent: ProxyConsents = {};
      if (claims && claims.length > 0) {
        consent.claims = claims;
      }
      if (consentPeriod) {
        consent.period = {
          start: consentPeriod.start ?? consentPeriod.startDate,
          end: consentPeriod.end ?? consentPeriod.endDate,
        };
      }

      const consents: ProxyConsents[] = Object.keys(consent).length > 0 ? [consent] : [];

      const requestData: UpdateProxyRequest = {
        identifier: rootIdentifier,
        proxies: [
          {
            identifier: proxyIdentifier,
            contactPoint: proxyContactPoint,
            consents,
            disclosureOption,
            disclosureOptionDescription,
            alcoholAbuseAuthorization,
            acknowledgement,
          },
        ],
      };

      const response = await updateProxyAPI(requestData) as UpdateProxyResponseWrapper;
      if (!response || response.status >= 400) {
        throw new Error(
          response?.error?.detail ||
            'Update proxy request failed.'
        );
      }

      return response;
    },
    [updateProxyAPI]
  );

  return {
    initiateProxy,
    terminateProxy,
    updateProxy,
    getPersonaType,
  };
};
